import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { confirmSecondaryInsuranceSchema } from "~/schemas/SignUp/InsuranceProvider";
import { RadioCardGroup } from "~/shared/components/ui/RadioCardGroup";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { YES_NO_OPTIONS } from "~/shared/utils/constants";
import { useSignUpStore } from "~/stores";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

interface ConfirmSecondaryInsuranceFormProps {
  onPrev: () => void;
  onNext: (response: string) => void;
}

export const ConfirmSecondaryInsuranceForm = ({
  onPrev,
  onNext,
  stepId = "primary_insurance",
}: ConfirmSecondaryInsuranceFormProps & { stepId?: string }) => {
  const { setShowInsuranceFinalStep } = useSignUpStore();
  const [selectedValue, setSelectedValue] = useState<string>("");
  // No persisted answer, so this step is always a fresh frontier: a yes/no
  // pick advances immediately.
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(false);

  const yesNoOptions = YES_NO_OPTIONS.map((option) => ({
    value: option,
    label: option,
  }));

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof confirmSecondaryInsuranceSchema>>({
    mode: "onBlur",
    resolver: zodResolver(confirmSecondaryInsuranceSchema),
    defaultValues: {
      hasSecondaryInsurance: "",
    },
  });

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: "secondary_insurance",
    subStep: "confirm_secondary",
  });

  const handleOptionChange = (value: string) => {
    setSelectedValue(value);
    setValue("hasSecondaryInsurance", value);
    if (autoAdvanceOnSelect) {
      void handleSubmit(onSubmit, onError)();
    }
  };

  const onSubmit: SubmitHandler<
    z.infer<typeof confirmSecondaryInsuranceSchema>
  > = (data) => {
    setShowInsuranceFinalStep(false);
    trackComplete({
      has_secondary_insurance: data.hasSecondaryInsurance === "Yes",
    });
    onNext(data.hasSecondaryInsurance);
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const canProceed = selectedValue !== "";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <h1 className="font-display text-xl sm:text-2xl">
          Do you have a secondary insurance provider?
        </h1>

        <RadioCardGroup
          options={yesNoOptions}
          value={selectedValue}
          onChange={handleOptionChange}
        />

        {errors.hasSecondaryInsurance && (
          <ErrorMessage message={errors.hasSecondaryInsurance.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons
        onPrev={() => {
          setShowInsuranceFinalStep(true);
          trackBack();
          onPrev();
        }}
        isNextDisabled={!canProceed}
        helperText="Select an option to continue"
      />
    </form>
  );
};
